import { Component, EventEmitter, inject, Input, Output, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Subscription, from, of } from 'rxjs';
import { catchError, map, mergeMap, toArray } from 'rxjs/operators';
import { PromptCardComponent } from '../../toasts-and-cards/prompt-card/prompt-card.component';
import { SettingsService } from '../../../services/settings/settings.service';
import { ITestContact } from '../../../types/settingsDataModule';
import { BroadcastMessageService } from '../../../services/broadcast/broadcast.service';
import { displayPhone } from '../../../pages/organization/settings/settings-roles';

/** Tests go out a few at a time; the endpoint takes one test contact per request. */
const PARALLEL_SENDS = 3;

/**
 * Who was ticked last time, kept for the session, so a second test (from
 * compose, a campaign or Smart broadcast) goes to the same people without
 * ticking them again.
 */
let rememberedIds = new Set<string>();

interface SendOutcome {
  contact: ITestContact;
  ok: boolean;
  reason: string;
}

/**
 * Sends the message being written to one or more test contacts, each as a
 * real SMS from the chosen sender ID. Used by compose, the campaign wizard
 * and Smart broadcast.
 */
@Component({
  selector: 'app-send-test-message-modal',
  standalone: true,
  imports: [PromptCardComponent, RouterLink],
  templateUrl: './send-test-message-modal.component.html',
  styleUrl: './send-test-message-modal.component.css',
})
export class SendTestMessageModalComponent implements OnInit, OnChanges, OnDestroy {
  settingsService = inject(SettingsService);
  broadcastService = inject(BroadcastMessageService);

  @Input() openModal: boolean = false;
  @Output() closeModalEvent = new EventEmitter();
  @Input() messageBody: string = '';
  @Input() senderId: string = '';

  testContacts: ITestContact[] = [];
  selected = new Set<string>();
  isLoading: boolean = false;

  // Feedback
  feedbackMessage: string = '';
  feedbackType: 'success' | 'error' | 'info' | 'warning' = 'success';
  showFeedback: boolean = false;

  private contactsSub?: Subscription;
  private sendSub?: Subscription;
  private closeTimer?: ReturnType<typeof setTimeout>;

  readonly displayPhone = displayPhone;

  get selectedCount(): number {
    return this.testContacts.filter((contact) => contact.id && this.selected.has(contact.id)).length;
  }

  get allSelected(): boolean {
    return this.testContacts.length > 0 && this.selectedCount === this.testContacts.length;
  }

  get buttonText(): string {
    const count = this.selectedCount;
    return count > 1 ? `Send to ${count} people` : 'Send test';
  }

  nameOf(contact: ITestContact): string {
    return `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || displayPhone(contact.primaryContactNumber) || 'Test contact';
  }

  isSelected(contact: ITestContact): boolean {
    return !!contact.id && this.selected.has(contact.id);
  }

  toggle(contact: ITestContact) {
    if (!contact.id || this.isLoading) return;
    const next = new Set(this.selected);
    next.has(contact.id) ? next.delete(contact.id) : next.add(contact.id);
    this.selected = next;
    this.showFeedback = false;
  }

  toggleAll() {
    if (this.isLoading) return;
    this.selected = this.allSelected
      ? new Set()
      : new Set(this.testContacts.map((contact) => contact.id).filter((id): id is string => !!id));
    this.showFeedback = false;
  }

  closeModal() {
    this.sendSub?.unsubscribe();
    clearTimeout(this.closeTimer);
    this.isLoading = false;
    this.closeModalEvent.emit();
    // Reset feedback when closing
    this.showFeedback = false;
    this.feedbackMessage = '';
  }

  onHandleSubmit() {
    // No test contacts at all: point to where they are added, not to a choice that does not exist.
    if (!this.testContacts.length) {
      this.displayFeedback('Add a test contact in Settings first, then open this again.', 'error');
      return;
    }

    const chosen = this.testContacts.filter((contact) => this.isSelected(contact));
    if (!chosen.length) {
      this.displayFeedback('Choose at least one test contact to send to.', 'error');
      return;
    }

    // Validate that message body exists
    if (!this.messageBody) {
      this.displayFeedback('Write the message before you send a test.', 'error');
      return;
    }

    // Validate that sender ID exists
    if (!this.senderId) {
      this.displayFeedback('Choose a sender ID before you send a test.', 'error');
      return;
    }

    rememberedIds = new Set(this.selected);
    this.isLoading = true;
    this.showFeedback = false;

    this.sendSub?.unsubscribe();
    this.sendSub = from(chosen)
      .pipe(
        mergeMap(
          (contact) =>
            this.broadcastService
              .sendTestMessage({ message: this.messageBody, senderId: this.senderId, testContactId: contact.id! })
              .pipe(
                map((): SendOutcome => ({ contact, ok: true, reason: '' })),
                catchError((err) =>
                  of<SendOutcome>({
                    contact,
                    ok: false,
                    reason: err?.error?.message || err?.error?.title || err?.message || 'not sent',
                  })
                )
              ),
          PARALLEL_SENDS
        ),
        toArray()
      )
      .subscribe((outcomes) => this.report(outcomes));
  }

  /** One line for the whole batch; failures stay ticked so "Send" retries just them. */
  private report(outcomes: SendOutcome[]) {
    this.isLoading = false;
    const sent = outcomes.filter((outcome) => outcome.ok);
    const failed = outcomes.filter((outcome) => !outcome.ok);

    if (!failed.length) {
      const who = sent.length === 1 ? this.nameOf(sent[0].contact) : `${sent.length} people`;
      this.displayFeedback(`Test sent to ${who}. Check the phones in a minute or two.`, 'success');
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closeModal(), 1500);
      return;
    }

    this.selected = new Set(failed.map((outcome) => outcome.contact.id!).filter(Boolean));
    const reasons = failed.map((outcome) => `${this.nameOf(outcome.contact)} (${outcome.reason})`).join(', ');
    if (!sent.length) {
      this.displayFeedback(
        failed.length === 1 ? `The test was not sent: ${failed[0].reason}` : `The test was not sent to anyone: ${reasons}.`,
        'error'
      );
      return;
    }
    this.displayFeedback(
      `Sent to ${sent.length} of ${outcomes.length}. Not sent to ${reasons}. Send again to retry.`,
      'error'
    );
  }

  displayFeedback(message: string, type: 'success' | 'error' | 'info' | 'warning') {
    this.feedbackMessage = message;
    this.feedbackType = type;
    this.showFeedback = true;
  }

  ngOnInit() {
    this.contactsSub = this.settingsService.getTestContacts.subscribe({
      next: (testContacts: ITestContact[]) => {
        this.testContacts = testContacts ?? [];
        this.restoreSelection();
      },
      error: () => {},
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Fetch test contacts whenever the modal opens
    if (changes['openModal'] && changes['openModal'].currentValue === true) {
      this.showFeedback = false;
      this.restoreSelection();
      this.settingsService.fetchTestContacts();
    }
  }

  ngOnDestroy() {
    this.contactsSub?.unsubscribe();
    this.sendSub?.unsubscribe();
    clearTimeout(this.closeTimer);
  }

  /** Last time's choice where those contacts still exist; a lone contact is simply ticked. */
  private restoreSelection() {
    const ids = new Set(this.testContacts.map((contact) => contact.id).filter((id): id is string => !!id));
    const kept = [...rememberedIds].filter((id) => ids.has(id));
    if (kept.length) this.selected = new Set(kept);
    else if (ids.size === 1) this.selected = new Set(ids);
    else this.selected = new Set([...this.selected].filter((id) => ids.has(id)));
  }
}
